import React, { useEffect, useMemo, useState } from 'react';
import { TextField } from '@mui/material';
import classnames from 'classnames';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from 'js/components/elements/editors/monaco/MonacoEditorUtils';
import { extractAtPath, parseMaybeJson } from './jsonPath';
import classes from './ResponsePreviewPanel.module.scss';

/**
 * Global "Response Preview" panel (prototype: right column, OUTSIDE the cards).
 * Per-tab behavior:
 *   - Raw: the raw probe response, no path input.
 *   - Data: a DATA PATH input (response.data.path) that progressively filters the
 *     JSON live as you type, showing the records at that path + a count.
 *   - Metadata: a METADATA PATH input (response.data.path.additional) showing the
 *     value at that path.
 *   - Headers: the response headers.
 *
 * USABILITY-ONLY: the path inputs write the same config keys via `onChange` →
 * `onAPIConfigChanged` for the active step. JSONPath filtering here is a
 * client-side preview only (does not affect serialization).
 */

export interface ResponsePreviewPanelProps {
  apiResponse?: any;
  stageConfig: Record<string, any>;
  onChange: (key: string, value: any) => void;
  /** controlled active tab (mirrors focus from the in-card path fields) */
  activeTab?: PreviewTab;
  onTabChange?: (tab: PreviewTab) => void;
  /** include the Data + Metadata (path) tabs. Off in template mode, where the
   *  data path is template-defined and not edited here. */
  dataTabs?: boolean;
}

type PreviewTab = 'raw' | 'data' | 'metadata' | 'headers' | 'errors';

const TABS: { id: PreviewTab; label: string }[] = [
  { id: 'raw', label: 'Raw' },
  { id: 'data', label: 'Data' },
  { id: 'metadata', label: 'Metadata' },
  { id: 'headers', label: 'Headers' },
];

const toText = (value: any): string => {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const ResponsePreviewPanel: React.FC<ResponsePreviewPanelProps> = ({
  apiResponse,
  stageConfig,
  onChange,
  activeTab,
  onTabChange,
  dataTabs = true,
}) => {
  const [internalTab, setInternalTab] = useState<PreviewTab>('data');
  const requestedTab = activeTab ?? internalTab;
  const setTab = (t: PreviewTab) => (onTabChange ? onTabChange(t) : setInternalTab(t));

  const contentType = stageConfig?.contentType;
  const dataPath: string = stageConfig?.['response.data.path'] ?? '';
  const metadataPath: string = stageConfig?.['response.data.path.additional'] ?? '';
  const rawContent: string = apiResponse?.apiResponseContent ?? '';
  const headers = apiResponse?.apiResponseHeaders;
  const headerCount = headers ? Object.keys(headers).length : undefined;

  // An HTTP response code >= 400 (or an explicit message) is a failure.
  const errorCode = apiResponse?.apiResponseCode;
  const errorMessage: string = apiResponse?.apiResponseMessage ?? '';
  const hasError = Number(errorCode) >= 400;
  // Template mode drops the Data/Metadata (path) tabs.
  const baseTabs = dataTabs ? TABS : TABS.filter((t) => t.id === 'raw' || t.id === 'headers');
  const tabs = hasError ? [...baseTabs, { id: 'errors' as PreviewTab, label: 'Errors' }] : baseTabs;
  // Resolve the displayed tab: fall back off Errors when the error clears, and off
  // Data/Metadata when those tabs are hidden.
  let tab: PreviewTab = requestedTab === 'errors' && !hasError ? 'data' : requestedTab;
  if (!dataTabs && (tab === 'data' || tab === 'metadata')) tab = 'raw';

  // Uncontrolled usage (e.g. template mode) auto-switches to Errors on failure.
  // Controlled usage drives this from the parent (see RestApiStepList).
  useEffect(() => {
    if (hasError && !onTabChange) setInternalTab('errors');
  }, [hasError, onTabChange]);

  const parsed = useMemo(() => parseMaybeJson(rawContent), [rawContent]);

  const dataValue = useMemo(() => extractAtPath(parsed, dataPath), [parsed, dataPath]);
  const dataCount = Array.isArray(dataValue) ? dataValue.length : undefined;
  const metadataValue = useMemo(() => extractAtPath(parsed, metadataPath), [parsed, metadataPath]);

  const editorLanguage = getEditorLanguage(contentType);

  let content: string;
  if (tab === 'errors') {
    content = rawContent || errorMessage;
  } else if (tab === 'raw') {
    content = typeof parsed === 'string' ? parsed : toText(parsed);
  } else if (tab === 'data') {
    content = toText(dataValue);
  } else if (tab === 'metadata') {
    content = toText(metadataValue);
  } else {
    content = toText(headers);
  }

  return (
    <div className={classes.panel} data-testid="response-preview-panel">
      <header className={classes.header}>
        <span className={classes.title}>Response Preview</span>
        {dataCount != null && (
          <span className={classes.recordBadge} data-testid="response-preview-count">
            {dataCount} records
          </span>
        )}
      </header>

      {hasError && (
        <div className={classes.errorBar} role="alert" data-testid="response-preview-error-bar">
          <span className={classes.errorBarDot} aria-hidden="true" />
          Request failed{errorCode ? ` (${errorCode})` : ''}
          {errorMessage ? ` — ${errorMessage}` : ''}
        </div>
      )}

      <div className={classes.tabBar}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={classnames(classes.tab, {
              [classes.tabActive]: tab === t.id,
              [classes.tabError]: t.id === 'errors',
            })}
            onClick={() => setTab(t.id)}
            data-testid={`response-preview-tab-${t.id}`}
          >
            {t.label}
            {t.id === 'data' && dataCount != null && (
              <span className={classes.tabCount}> · {dataCount}</span>
            )}
            {t.id === 'headers' && headerCount != null && (
              <span className={classes.tabCount}> · {headerCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Path input only on Data / Metadata tabs (Raw + Headers have none). */}
      {tab === 'data' && (
        <div className={classes.dataPathRow}>
          <span className={classes.dataPathLabel}>DATA PATH</span>
          <TextField
            size="small"
            fullWidth
            value={dataPath}
            placeholder="$.results[*]"
            onChange={(e) => onChange('response.data.path', e.target.value)}
            inputProps={{ 'aria-label': 'Data path', 'data-testid': 'response-preview-data-path' }}
          />
          {dataCount != null && <span className={classes.dataPathCount}>{dataCount} rec</span>}
        </div>
      )}
      {tab === 'metadata' && (
        <div className={classes.dataPathRow}>
          <span className={classes.dataPathLabel}>METADATA PATH</span>
          <TextField
            size="small"
            fullWidth
            value={metadataPath}
            placeholder="$.paging"
            onChange={(e) => onChange('response.data.path.additional', e.target.value)}
            inputProps={{
              'aria-label': 'Metadata path',
              'data-testid': 'response-preview-metadata-path',
            }}
          />
        </div>
      )}

      <div className={classes.viewer} data-testid="response-preview-viewer">
        {content ? (
          <LoadableMonacoEditor
            language={editorLanguage}
            theme="dark"
            value={content}
            height="100%"
            options={{ readOnly: true, minimap: { enabled: false } }}
          />
        ) : (
          <div className={classes.empty} />
        )}
      </div>
    </div>
  );
};

export default ResponsePreviewPanel;
